import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, Plus, ChevronDown, ChevronUp, AlertCircle, Loader2, Pencil, Power, Search, MoreHorizontal, Layers } from 'lucide-react';
import { FeaturedLayout } from '../layouts';
import { createCategoria, getCategorias, getCategoriaGroupLinks, removeCategoriaFromGroups, setCategoriaActiva, updateCategoria } from '../../lib/supabase';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { useModal } from '../../hooks/useModal';
import Modal from '../ui/Modal';
import { DEFAULT_SPORT, normalizeSportValue } from '../../constants/sports';
import { getOrgDeportes } from '../../lib/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useDeportes } from '../../hooks/useDeportes';
import { opcionesDeporte, deportePorDefecto, SIN_DEPORTES_MSG } from '../../lib/deportesOrg';
import DeporteFijo from '../deportes/DeporteFijo';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import { validateTackleLimits, rangoCategoria, ecoLimiteTackle } from '../../lib/categorias';

const STATUS_FILTERS = [
  { value: 'activas', label: 'Activas' },
  { value: 'inactivas', label: 'Inactivas' },
  { value: 'todas', label: 'Todas' }
];

const EMPTY_FORM = {
  id: null,
  nombre: '',
  deporte: '',
  modo_calculo: 'edad',
  anio_inicio: '',
  anio_fin: '',
  edad_min: '',
  edad_max: '',
  tackle_limite_sup_dia: '',
  tackle_limite_sup_mes: '',
  tackle_edad_sup: '',
  tackle_limite_inf_dia: '',
  tackle_limite_inf_mes: '',
  tackle_edad_inf: '',
  tackle_peso_max_kg: '',
  descripcion: ''
};

const normalizeNumberField = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const validateAgeYearConsistency = (payload, currentYear) => {
  if (normalizeSportValue(payload.deporte || DEFAULT_SPORT) === 'tackle') return null;

  const { edad_min, edad_max, anio_inicio, anio_fin } = payload;
  if (edad_min !== null && anio_inicio !== null) {
    const expected = currentYear - anio_inicio;
    if (edad_min !== expected) {
      return 'La edad mínima no coincide con el año máximo.';
    }
  }
  if (edad_max !== null && anio_fin !== null) {
    const expected = currentYear - anio_fin;
    if (edad_max !== expected) {
      return 'La edad máxima no coincide con el año mínimo.';
    }
  }
  return null;
};

// La etiqueta sale del modulo compartido. La copia que vivia aqui invertia dos cosas:
// escribia "Nacidos 2017-2016" en flag (el rango va fin-inicio), y en tackle con un
// solo extremo decia "Nacidos hasta {sup}" cuando sup es el jugador MAS GRANDE, o sea
// la fecha mas TEMPRANA. Y ese texto se guardaba en `descripcion`.
const formatRangeLabel = (categoria) => rangoCategoria(categoria, { conEdades: true });

const buildFormFromCategoria = (categoria) => ({
  id: categoria.id,
  nombre: categoria.nombre || '',
  deporte: normalizeSportValue(categoria.deporte || 'flag'),
  modo_calculo:
    categoria.edad_min !== null && categoria.edad_min !== undefined
      ? 'edad'
      : categoria.edad_max !== null && categoria.edad_max !== undefined
        ? 'edad'
        : categoria.anio_inicio !== null && categoria.anio_inicio !== undefined
          ? 'anio'
          : categoria.anio_fin !== null && categoria.anio_fin !== undefined
            ? 'anio'
            : 'edad',
  anio_inicio: categoria.anio_inicio ?? '',
  anio_fin: categoria.anio_fin ?? '',
  edad_min: categoria.edad_min ?? '',
  edad_max: categoria.edad_max ?? '',
  tackle_limite_sup_dia: categoria.tackle_limite_sup_dia ?? '',
  tackle_limite_sup_mes: categoria.tackle_limite_sup_mes ?? '',
  tackle_edad_sup: categoria.tackle_edad_sup ?? '',
  tackle_limite_inf_dia: categoria.tackle_limite_inf_dia ?? '',
  tackle_limite_inf_mes: categoria.tackle_limite_inf_mes ?? '',
  tackle_edad_inf: categoria.tackle_edad_inf ?? '',
  tackle_peso_max_kg: categoria.tackle_peso_max_kg ?? '',
  descripcion: categoria.descripcion || ''
});

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { modal, showConfirm, showError, showSuccess, closeModal } = useModal();
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('activas');
  const [showFilters, setShowFilters] = useState(false);
  const { org } = useOrganization();
  const { label: nombreDeporte } = useDeportes();
  const [orgDeportes, setOrgDeportes] = useState([]);
  const [deportesCargados, setDeportesCargados] = useState(false);

  // Solo los deportes habilitados de la org, no una lista fija
  useEffect(() => {
    if (!org?.id) return;
    getOrgDeportes(org.id).then(({ data }) => {
      if (data) setOrgDeportes(data);
      setDeportesCargados(true);
    });
  }, [org?.id]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorForm, setEditorForm] = useState(EMPTY_FORM);
  const [editorError, setEditorError] = useState('');
  const guard = useUnsavedGuard({ onClose: () => setEditorOpen(false) });
  const currentYear = new Date().getFullYear();
  const realtimeValidation = useMemo(() => {
    const deporte = normalizeSportValue(editorForm.deporte || DEFAULT_SPORT);
    if (deporte === 'tackle') {
      const supAge = normalizeNumberField(editorForm.tackle_edad_sup);
      const infAge = normalizeNumberField(editorForm.tackle_edad_inf);
      if (supAge !== null && infAge !== null && supAge < infAge) {
        return 'La edad limite superior no puede ser menor que la edad limite inferior.';
      }
      return '';
    }

    const edadMin = normalizeNumberField(editorForm.edad_min);
    const edadMax = normalizeNumberField(editorForm.edad_max);
    if (edadMin !== null && edadMax !== null && edadMin > edadMax) {
      return 'La edad minima no puede ser mayor que la edad maxima.';
    }

    const anioInicio = normalizeNumberField(editorForm.anio_inicio);
    const anioFin = normalizeNumberField(editorForm.anio_fin);
    if (anioInicio !== null && anioFin !== null && anioInicio < anioFin) {
      return 'El año maximo no puede ser menor que el año minimo.';
    }

    return '';
  }, [
    editorForm.deporte,
    editorForm.edad_min,
    editorForm.edad_max,
    editorForm.anio_inicio,
    editorForm.anio_fin,
    editorForm.tackle_edad_sup,
    editorForm.tackle_edad_inf
  ]);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await getCategorias({ includeInactive: true });
      if (fetchError) throw fetchError;
      setCategories(data || []);
    } catch (fetchError) {
      console.error('Error loading categories:', fetchError);
      setError('No pudimos cargar las categorías. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filteredCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return categories.filter((categoria) => {
      if (statusFilter === 'activas' && categoria.activo !== true) return false;
      if (statusFilter === 'inactivas' && categoria.activo !== false) return false;
      if (
        sportFilter !== 'todos' &&
        normalizeSportValue(categoria.deporte) !== normalizeSportValue(sportFilter)
      ) {
        return false;
      }
      if (!normalizedSearch) return true;
      return (
        categoria.nombre?.toLowerCase().includes(normalizedSearch) ||
        categoria.descripcion?.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [categories, search, sportFilter, statusFilter]);

  const openCreate = () => {
    setEditorForm({ ...EMPTY_FORM, deporte: deportePorDefecto(orgDeportes) });
    setEditorError('');
    guard.resetDirty();
    setEditorOpen(true);
  };

  const openEdit = (categoria) => {
    setEditorForm(buildFormFromCategoria(categoria));
    setEditorError('');
    guard.resetDirty();
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!editorForm.nombre.trim()) {
      setEditorError('El nombre de la categoría es obligatorio.');
      return;
    }

    if (!editorForm.deporte) {
      setEditorError(orgDeportes.length === 0 ? SIN_DEPORTES_MSG : 'Elige el deporte de la categoría.');
      return;
    }

    const normalizedForm =
      normalizeSportValue(editorForm.deporte) === 'tackle'
        ? { ...editorForm }
        : editorForm.modo_calculo === 'anio'
          ? syncFromAnio(editorForm)
          : syncFromEdad(editorForm);

    const payloadBase = {
      nombre: normalizedForm.nombre.trim(),
      deporte: normalizedForm.deporte,
      anio_inicio:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? null
          : normalizeNumberField(normalizedForm.anio_inicio),
      anio_fin:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? null
          : normalizeNumberField(normalizedForm.anio_fin),
      edad_min:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? null
          : normalizeNumberField(normalizedForm.edad_min),
      edad_max:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? null
          : normalizeNumberField(normalizedForm.edad_max),
      tackle_limite_sup_dia:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_limite_sup_dia)
          : null,
      tackle_limite_sup_mes:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_limite_sup_mes)
          : null,
      tackle_edad_sup:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_edad_sup)
          : null,
      tackle_limite_inf_dia:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_limite_inf_dia)
          : null,
      tackle_limite_inf_mes:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_limite_inf_mes)
          : null,
      tackle_edad_inf:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_edad_inf)
          : null,
      tackle_peso_max_kg:
        normalizeSportValue(normalizedForm.deporte) === 'tackle'
          ? normalizeNumberField(normalizedForm.tackle_peso_max_kg)
          : null,
    };
    const autoDescripcion = formatRangeLabel(payloadBase);
    const payload = {
      ...payloadBase,
      descripcion: autoDescripcion || null
    };

    const consistencyError = validateAgeYearConsistency(payload, currentYear);

    const tackleError = validateTackleLimits(payload);
    if (consistencyError) {
      setEditorError(consistencyError);
      return;
    }

    if (tackleError) {
      setEditorError(tackleError);
      return;
    }

    if (payload.edad_min !== null && payload.edad_max !== null && payload.edad_min > payload.edad_max) {
      setEditorError('La edad minima no puede ser mayor que la edad maxima.');
      return;
    }

    if (
      payload.anio_inicio !== null &&
      payload.anio_fin !== null &&
      payload.anio_inicio < payload.anio_fin
    ) {
      setEditorError('El año maximo no puede ser menor que el año minimo.');
      return;
    }

    setSaving(true);
    setError('');
    setEditorError('');
    try {
      if (!editorForm.id) {
        const normalizedName = (payload.nombre || '').trim().toLowerCase();
        const alreadyExists = categories.some(
          (cat) => (cat?.nombre || '').trim().toLowerCase() === normalizedName
        );
        if (alreadyExists) {
          setEditorError('Ya existe una categoría con ese nombre. Usa un nombre distinto.');
          setSaving(false);
          return;
        }
      }

      const result = editorForm.id
        ? await updateCategoria(editorForm.id, payload)
        : await createCategoria(payload);

      if (result.error) throw result.error;

      guard.resetDirty();
      setEditorOpen(false);
      setEditorForm({ ...EMPTY_FORM });
      setEditorError('');
      await loadCategories();
    } catch (saveError) {
      console.error('Error saving category:', saveError);
      const isDuplicateName =
        saveError?.code === '23505' &&
        typeof saveError?.message === 'string' &&
        saveError.message.includes('categorias_nombre_key');
      if (isDuplicateName) {
        setEditorError('Ya existe una categoría con ese nombre. Usa un nombre distinto.');
        return;
      }
      setError(
        saveError?.message
          ? `No se pudo guardar la categoría: ${saveError.message}`
          : 'No se pudo guardar la categoría.'
      );
    } finally {
      setSaving(false);
    }
  };

  const syncFromEdad = useCallback(
    (next) => {
      const edadMin = normalizeNumberField(next.edad_min);
      const edadMax = normalizeNumberField(next.edad_max);
      return {
        ...next,
        anio_inicio: edadMin === null ? '' : String(currentYear - edadMin),
        anio_fin: edadMax === null ? '' : String(currentYear - edadMax)
      };
    },
    [currentYear]
  );

  const syncFromAnio = useCallback(
    (next) => {
      const anioInicio = normalizeNumberField(next.anio_inicio);
      const anioFin = normalizeNumberField(next.anio_fin);
      return {
        ...next,
        edad_min: anioInicio === null ? '' : String(currentYear - anioInicio),
        edad_max: anioFin === null ? '' : String(currentYear - anioFin)
      };
    },
    [currentYear]
  );

  const handleToggleActive = async (categoria) => {
    const nextValue = !categoria.activo;
    const actionLabel = nextValue ? 'activar' : 'deshabilitar';

    try {
      if (!nextValue) {
        const { data: groupLinks, error: linkError } = await getCategoriaGroupLinks(categoria.id);
        if (linkError) throw linkError;
        const groupCount = groupLinks?.groupIds?.length || 0;
        const groupNames = groupLinks?.groupNames || [];
        const groupsLabel =
          groupNames.length > 0 ? `Grupos: ${groupNames.join(', ')}` : '';
        const confirmMessage =
          groupCount > 0
            ? `Esta categoría está asignada a ${groupCount} grupo(s). ${groupsLabel} Al deshabilitarla se quitará de esos grupos. ¿Deseas continuar?`
            : `¿Seguro que quieres ${actionLabel} la categoría "${categoria.nombre}"?`;
        const confirmed = await showConfirm(
          'Deshabilitar categoría',
          confirmMessage,
          'Deshabilitar',
          'Cancelar'
        );
        if (!confirmed) return;
        if (groupCount > 0) {
          const { error: removeError } = await removeCategoriaFromGroups(categoria.id);
          if (removeError) throw removeError;
        }
      } else {
        const confirmed = await showConfirm(
          'Activar categoría',
          `¿Seguro que quieres ${actionLabel} la categoría "${categoria.nombre}"?`,
          'Activar',
          'Cancelar'
        );
        if (!confirmed) return;
      }
      const { error: toggleError } = await setCategoriaActiva(categoria.id, nextValue);
      if (toggleError) throw toggleError;
      await loadCategories();
    } catch (toggleError) {
      console.error('Error toggling category status:', toggleError);
      showError('Error', 'No se pudo actualizar el estatus de la categoría.');
    }
  };

  return (
    <FeaturedLayout
      title="Gestión de categorías"
      subtitle="Administra categorías y rangos de edad por deporte."
      refreshAction={loadCategories}
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar categoría..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
              <span className="font-medium text-foreground">{filteredCategories.length}</span>{' '}
              {filteredCategories.length === 1 ? 'categoría' : 'categorías'}
            </span>
            <Button
              variant="outline"
              onClick={() => setShowFilters((prev) => !prev)}
              className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
            >
              <Filter className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Filtros</span>
              {showFilters
                ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" />
                : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
              {(sportFilter !== 'todos' || statusFilter !== 'activas') && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
              )}
            </Button>
            <Button onClick={openCreate} className="h-10" aria-label="Nueva categoría" title="Nueva categoría">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nueva categoría</span>
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="grid gap-3 lg:grid-cols-3">
            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todos los deportes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los deportes</SelectItem>
                {opcionesDeporte(orgDeportes).map((sport) => (
                  <SelectItem key={sport.value} value={sport.value}>
                    {sport.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setSportFilter('todos');
                setStatusFilter('activas');
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Cargando categorías...</span>
          </div>
        ) : filteredCategories.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Layers className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground mb-2">
                {categories.length === 0 ? 'Todavía no hay categorías' : 'No se encontraron categorías'}
              </p>
              {categories.length === 0 ? (
                <Button size="sm" className="mt-2" onClick={openCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear la primera
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Ajusta la búsqueda o quita los filtros para ver otras categorías.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {filteredCategories.map((categoria) => (
              <Card key={categoria.id} className="flex flex-col h-full transition-shadow hover:shadow-md">
                <CardContent className="p-4 flex flex-col gap-2 flex-grow">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold leading-tight truncate">
                        {categoria.nombre || 'Categoría sin nombre'}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {formatRangeLabel(categoria)}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 gap-1.5 font-normal text-[11px] px-2 py-0.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${categoria.activo ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                      {categoria.activo ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="secondary" className="text-[11px] font-normal">
                      {nombreDeporte(categoria.deporte)}
                    </Badge>
                  </div>

                  {categoria.descripcion && (
                    <p className="text-xs text-muted-foreground truncate">{categoria.descripcion}</p>
                  )}

                  <div className="flex gap-2 pt-1 mt-auto">
                    <Button size="sm" className="flex-1 h-8" onClick={() => openEdit(categoria)}>
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      Editar
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Más acciones">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleToggleActive(categoria)}
                          className={categoria.activo ? 'text-destructive focus:text-destructive' : ''}
                        >
                          <Power className="h-4 w-4" />
                          {categoria.activo ? 'Deshabilitar categoría' : 'Activar categoría'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={editorOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editorForm.id ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>
            <DialogDescription>
              {normalizeSportValue(editorForm.deporte) === 'tackle'
                ? 'Define hasta dónde llega la categoría por edad, con su fecha de corte.'
                : 'Define los límites por edad o por año de nacimiento para esta categoría.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="mb-1 block">Nombre *</Label>
              <Input
                value={editorForm.nombre}
                onChange={(event) => setEditorForm((prev) => ({ ...prev, nombre: event.target.value }))}
                placeholder="Ej. U10"
              />
            </div>

            <div>
              <Label className="mb-1 block">Deporte</Label>
              {editorForm.id ? (
                <DeporteFijo nombre={nombreDeporte(editorForm.deporte)} crea="otra categoría" />
              ) : (
                <>
                  <Select
                    value={editorForm.deporte}
                    onValueChange={(value) => {
                      guard.markDirty();
                      setEditorForm((prev) => ({
                        ...prev,
                        deporte: value
                      }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={!deportesCargados ? 'Cargando deportes...' : 'Seleccionar deporte'} />
                    </SelectTrigger>
                    <SelectContent>
                      {opcionesDeporte(orgDeportes, editorForm.deporte, nombreDeporte).map((option) => (
                        <SelectItem key={option.value} value={option.value} disabled={option.deshabilitado}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {deportesCargados && orgDeportes.length === 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">{SIN_DEPORTES_MSG}</p>
                  )}
                </>
              )}
            </div>

            {normalizeSportValue(editorForm.deporte) === 'tackle' ? (
              <div className="space-y-5">
                {/* Cada limite se lee como una FRASE: "13 años cumplidos antes del 31/7".
                    Antes eran seis campos sueltos con una advertencia permanente ("Años,
                    no año de nacimiento") que existia porque el diseño permitia el error
                    en vez de evitarlo. Ahora lo evita el eco de abajo: escribir 2016 donde
                    va la edad muestra "nacidos desde el 1 de enero de 10", que salta a la
                    vista.

                    Y ojo con la semantica, que el texto anterior tenia AL REVES: con edad
                    maxima 13, un jugador de 13 SI juega. Queda fuera quien ya la cumplio
                    ANTES del corte. */}
                {[
                  {
                    clave: 'sup',
                    titulo: 'Edad máxima',
                    ayuda: 'El jugador más grande que admite la categoría.',
                    conector: 'cumplidos antes del',
                    extremo: 'max',
                    ejemploEdad: '13',
                    ejemploDia: '31',
                    ejemploMes: '7',
                  },
                  {
                    clave: 'inf',
                    titulo: 'Edad mínima',
                    ayuda: 'El más chico que admite.',
                    conector: 'cumplidos para el',
                    extremo: 'min',
                    ejemploEdad: '12',
                    ejemploDia: '1',
                    ejemploMes: '1',
                  },
                ].map((lim) => {
                  const campoEdad = `tackle_edad_${lim.clave}`;
                  const campoDia = `tackle_limite_${lim.clave}_dia`;
                  const campoMes = `tackle_limite_${lim.clave}_mes`;
                  const eco = ecoLimiteTackle(
                    normalizeNumberField(editorForm[campoEdad]),
                    normalizeNumberField(editorForm[campoDia]),
                    normalizeNumberField(editorForm[campoMes]),
                    lim.extremo
                  );
                  return (
                    <div key={lim.clave} className="rounded-lg border border-border bg-muted/50 p-4">
                      <p className="text-sm font-semibold text-foreground">{lim.titulo}</p>
                      <p className="text-xs text-muted-foreground">{lim.ayuda}</p>

                      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2">
                        <Input
                          type="number"
                          min="1"
                          max="60"
                          className="w-20"
                          aria-label={`${lim.titulo} en años`}
                          placeholder={lim.ejemploEdad}
                          value={editorForm[campoEdad]}
                          onChange={(event) =>
                            setEditorForm((prev) => ({ ...prev, [campoEdad]: event.target.value }))
                          }
                        />
                        <span className="text-sm text-muted-foreground">años {lim.conector}</span>
                        <Input
                          type="number"
                          min="1"
                          max="31"
                          className="w-16"
                          aria-label="Día de corte"
                          placeholder={lim.ejemploDia}
                          value={editorForm[campoDia]}
                          onChange={(event) =>
                            setEditorForm((prev) => ({ ...prev, [campoDia]: event.target.value }))
                          }
                        />
                        <span className="text-sm text-muted-foreground">/</span>
                        <Input
                          type="number"
                          min="1"
                          max="12"
                          className="w-16"
                          aria-label="Mes de corte"
                          placeholder={lim.ejemploMes}
                          value={editorForm[campoMes]}
                          onChange={(event) =>
                            setEditorForm((prev) => ({ ...prev, [campoMes]: event.target.value }))
                          }
                        />
                        <span className="text-xs text-muted-foreground">día / mes (opcional)</span>
                      </div>

                      <p className="mt-2 text-xs text-muted-foreground">
                        {eco || 'Captura la edad para ver a quién admite.'}
                      </p>
                    </div>
                  );
                })}

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block">Peso máximo (kg)</Label>
                    <p className="text-xs text-muted-foreground">Límite de peso para la categoría.</p>
                    <Input
                      type="number"
                      placeholder="Ej. 66"
                      value={editorForm.tackle_peso_max_kg}
                      onChange={(event) =>
                        setEditorForm((prev) => ({
                          ...prev,
                          tackle_peso_max_kg: event.target.value
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-2">
                  <Label className="mb-1 block">Definir por</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={editorForm.modo_calculo === 'edad' ? 'default' : 'outline'}
                      onClick={() => {
                        guard.markDirty();
                        setEditorForm((prev) => {
                          const next = { ...prev, modo_calculo: 'edad' };
                          return syncFromEdad(next);
                        });
                      }}
                    >
                      Edad
                    </Button>
                    <Button
                      type="button"
                      variant={editorForm.modo_calculo === 'anio' ? 'default' : 'outline'}
                      onClick={() => {
                        guard.markDirty();
                        setEditorForm((prev) => {
                          const next = { ...prev, modo_calculo: 'anio' };
                          return syncFromAnio(next);
                        });
                      }}
                    >
                      Año de nacimiento
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Limite inferior</p>
                    <p className="text-xs text-muted-foreground">Menor edad permitida (mas jovenes).</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {editorForm.modo_calculo === 'edad' ? (
                      <div>
                        <Label className="mb-1 block">Edad minima</Label>
                        <p className="text-xs text-muted-foreground">Edad mas joven permitida.</p>
                        <Input
                          type="number"
                          value={editorForm.edad_min}
                          onChange={(event) =>
                            setEditorForm((prev) => {
                              const next = { ...prev, edad_min: event.target.value };
                              return syncFromEdad(next);
                            })
                          }
                          placeholder="Ej. 8"
                        />
                        {normalizeNumberField(editorForm.edad_min) !== null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Equivale a año: {currentYear - normalizeNumberField(editorForm.edad_min)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <Label className="mb-1 block">Año maximo (mas reciente)</Label>
                        <p className="text-xs text-muted-foreground">Año mas reciente permitido.</p>
                        <Input
                          type="number"
                          value={editorForm.anio_inicio}
                          onChange={(event) =>
                            setEditorForm((prev) => {
                              const next = { ...prev, anio_inicio: event.target.value };
                              return syncFromAnio(next);
                            })
                          }
                          placeholder="Ej. 2015"
                        />
                        {normalizeNumberField(editorForm.anio_inicio) !== null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Equivale a edad: {currentYear - normalizeNumberField(editorForm.anio_inicio)} años
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Limite superior</p>
                    <p className="text-xs text-muted-foreground">Mayor edad permitida.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {editorForm.modo_calculo === 'edad' ? (
                      <div>
                        <Label className="mb-1 block">Edad maxima</Label>
                        <p className="text-xs text-muted-foreground">Edad mas antigua permitida.</p>
                        <Input
                          type="number"
                          value={editorForm.edad_max}
                          onChange={(event) =>
                            setEditorForm((prev) => {
                              const next = { ...prev, edad_max: event.target.value };
                              return syncFromEdad(next);
                            })
                          }
                          placeholder="Ej. 10"
                        />
                        {normalizeNumberField(editorForm.edad_max) !== null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Equivale a año: {currentYear - normalizeNumberField(editorForm.edad_max)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <Label className="mb-1 block">Año minimo (mas antiguo)</Label>
                        <p className="text-xs text-muted-foreground">Año mas antiguo permitido.</p>
                        <Input
                          type="number"
                          value={editorForm.anio_fin}
                          onChange={(event) =>
                            setEditorForm((prev) => {
                              const next = { ...prev, anio_fin: event.target.value };
                              return syncFromAnio(next);
                            })
                          }
                          placeholder="Ej. 2016"
                        />
                        {normalizeNumberField(editorForm.anio_fin) !== null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Equivale a edad: {currentYear - normalizeNumberField(editorForm.anio_fin)} años
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {(editorError || realtimeValidation) && (
              <div className="text-sm text-red-600">{editorError || realtimeValidation}</div>
            )}
            <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              {formatRangeLabel({
                deporte: normalizeSportValue(editorForm.deporte || DEFAULT_SPORT),
                anio_inicio: normalizeNumberField(editorForm.anio_inicio),
                anio_fin: normalizeNumberField(editorForm.anio_fin),
                edad_min: normalizeNumberField(editorForm.edad_min),
                edad_max: normalizeNumberField(editorForm.edad_max),
                tackle_limite_sup_dia: normalizeNumberField(editorForm.tackle_limite_sup_dia),
                tackle_limite_sup_mes: normalizeNumberField(editorForm.tackle_limite_sup_mes),
                tackle_edad_sup: normalizeNumberField(editorForm.tackle_edad_sup),
                tackle_limite_inf_dia: normalizeNumberField(editorForm.tackle_limite_inf_dia),
                tackle_limite_inf_mes: normalizeNumberField(editorForm.tackle_limite_inf_mes),
                tackle_edad_inf: normalizeNumberField(editorForm.tackle_edad_inf),
                tackle_peso_max_kg: normalizeNumberField(editorForm.tackle_peso_max_kg)
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        showCancel={modal.showCancel}
      />
    </FeaturedLayout>
  );
};

export default CategoriesManager;























